import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, FileText } from 'lucide-react';

export const MAIL_API = `${process.env.REACT_APP_BACKEND_URL}/api/mail`;
// the shared mailbox the Mail page is showing (AP@, sales@ …); empty = your own
export const getMailbox = () => sessionStorage.getItem('ops_mailbox') || '';
export const setMailbox = (email) => (email ? sessionStorage.setItem('ops_mailbox', email) : sessionStorage.removeItem('ops_mailbox'));
export const mailAuth = (ownOnly = false) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const mb = ownOnly ? '' : getMailbox();
  if (mb) headers['X-Mailbox'] = mb;
  return { headers };
};
export const attachmentUrl = (a) => `${MAIL_API}/attachments/${a.message_id}/${a.id}?name=${encodeURIComponent(a.filename || '')}&token=${localStorage.getItem('token')}${getMailbox() ? `&mailbox=${encodeURIComponent(getMailbox())}` : ''}`;

// light glass panel used by every mail surface
export const PANEL = 'rounded-2xl ti-panel';

export const FOLDERS = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'starred', label: 'Starred' },
  { id: 'important', label: 'Important' },
  { id: 'sent', label: 'Sent' },
  { id: 'drafts', label: 'Drafts' },
  { id: 'all', label: 'All mail' },
  { id: 'spam', label: 'Spam' },
  { id: 'trash', label: 'Trash' },
];

// quick filter chips → Gmail query fragments (fiscal year rolls 26- → 27- on Oct 31; both are "job mail")
export const QUICK_FILTERS = [
  { id: 'unread', label: 'Unread', q: 'is:unread' },
  { id: 'attachments', label: 'Attachments', q: 'has:attachment' },
  { id: 'starred', label: 'Starred', q: 'is:starred' },
  { id: 'jobs', label: 'Job mail', q: '("26-" OR "27-" OR "25-")' },
  { id: 'fromme', label: 'From me', q: 'from:me' },
  { id: 'week', label: 'This week', q: 'newer_than:7d' },
];

export const CATEGORY_META = {
  customer: { label: 'Customers', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  vendor: { label: 'Vendors / Suppliers', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  subcontractor: { label: 'Subcontractors', cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  engineer: { label: 'Engineers / Consultants', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  team: { label: '365 Team', cls: 'bg-slate-800 text-white border-slate-800' },
  other: { label: 'Other', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  uncategorised: { label: 'Uncategorised', cls: 'bg-white text-slate-500 border-dashed border-slate-300' },
};
export const CATEGORY_ORDER = ['customer', 'vendor', 'subcontractor', 'engineer', 'team', 'other', 'uncategorised'];

export const SHORTCUTS = [
  ['j / k', 'Next / previous conversation'], ['Enter / o', 'Open'], ['u / Esc', 'Back to list'], ['x', 'Select / unselect'],
  ['e', 'Archive'], ['#', 'Delete'], ['s', 'Star'], ['U', 'Mark unread'], ['r / a / f', 'Reply / reply all / forward'],
  ['c', 'Compose'], ['/', 'Search'], ['l', 'Label as…'], ['v', 'Move to…'], ['n / p', 'Newer / older message in thread'],
  ['End', 'Jump to newest message'], ['g then i / s / t', 'Go to Inbox / Sent / Starred'], ['?', 'This help'],
];

export const REF_IN_SUBJECT = /\b2[4-9]\s?[-–]\s?\d{1,4}\b/;
export const withRef = (ref, subject) => {
  const m = (subject || '').match(/^((?:\s*(?:re|fwd?|fw)\s*:)+)\s*(.*)$/i);
  return m ? `${m[1].trim()} [${ref}] ${m[2]}`.trim() : `[${ref}] ${subject || ''}`.trim();
};

export const labelName = (labels, id) => labels?.find(l => l.id === id)?.name || id;

export const fmtMailDate = (iso, long = false) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (long) return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (now - d < 6 * 86400000) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const fmtSize = (n) => (!n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);

export const addrLabel = (a) => (a?.name ? a.name : (a?.email || '').split('@')[0]);

export const JobChip = ({ chip, size = 'xs' }) => {
  const isWo = chip.type === 'wo';
  const to = isWo ? `/projects/${chip.ref}` : `/quotes?search=${chip.ref}`;
  const cls = `inline-flex items-center gap-1 rounded-md font-mono font-bold border transition-colors ${size === 'xs' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} ${isWo ? 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`;
  return (
    <Link to={to} onClick={e => e.stopPropagation()} className={cls} title={`${isWo ? 'Work order' : 'Quote'} ${chip.ref}${chip.customer ? ' — ' + chip.customer : ''}${chip.quote ? ' · quote ' + chip.quote : ''}`} data-testid={`mail-chip-${chip.ref}`}>
      {isWo ? <Briefcase className="w-3 h-3" /> : <FileText className="w-3 h-3" />}{chip.ref}
    </Link>
  );
};
